/**
 * Product Quick View Modal
 * Opens product page content in a modal popup when clicking product cards
 */

class ProductQuickView {
  constructor() {
    this.modal = document.getElementById('product-quick-view-modal');
    this.modalContent = this.modal?.querySelector('[data-modal-content]');
    this.closeButtons = this.modal?.querySelectorAll('[data-modal-close]');
    this.triggers = [];
    this.currentProductHandle = null;
    
    this.init();
  }

  init() {
    if (!this.modal) {
      console.warn('Product Quick View Modal not found');
      return;
    }

    // Store scrollbar width for body padding
    this.setScrollbarWidth();
    
    // Bind close buttons
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.close();
      }
    });

    // Initialize triggers
    this.bindTriggers();

    // Re-bind triggers on dynamic content load
    document.addEventListener('shopify:section:load', () => {
      this.bindTriggers();
    });
  }

  setScrollbarWidth() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
  }

  bindTriggers() {
    // Find all quick view triggers
    const newTriggers = document.querySelectorAll('[data-quick-view-trigger]');
    
    newTriggers.forEach(trigger => {
      // Remove old listener if exists
      if (trigger._quickViewListener) {
        trigger.removeEventListener('click', trigger._quickViewListener);
      }

      // Create new listener
      trigger._quickViewListener = (e) => {
        e.preventDefault();
        const productHandle = trigger.getAttribute('data-product-handle');
        if (productHandle) {
          this.open(productHandle);
        }
      };

      // Add new listener
      trigger.addEventListener('click', trigger._quickViewListener);
    });

    this.triggers = Array.from(newTriggers);
  }

  async open(productHandle) {
    if (!productHandle) return;

    this.currentProductHandle = productHandle;

    // Show modal with loading state
    this.modal.classList.add('active');
    document.body.classList.add('modal-open');

    // Show loading spinner
    this.modalContent.innerHTML = `
      <div class="product-quick-view-modal__loading">
        <div class="product-quick-view-modal__spinner"></div>
      </div>
    `;

    try {
      // Fetch product page HTML
      const response = await fetch(`/products/${productHandle}?view=quick-view`, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product');
      }

      const html = await response.text();
      
      // Parse HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract the main product content
      const productContent = doc.querySelector('.enhanced-product-page__grid');

      if (productContent) {
        // Create modal grid structure
        this.modalContent.innerHTML = `
          <div class="product-quick-view-modal__product-grid">
            ${productContent.innerHTML}
          </div>
        `;

        // Re-initialize any product scripts
        this.initializeProductScripts();

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('quickview:loaded', {
          detail: { productHandle: productHandle }
        }));
      } else {
        throw new Error('Product content not found');
      }

    } catch (error) {
      console.error('Error loading quick view:', error);
      this.modalContent.innerHTML = `
        <div style="padding: 40px; text-align: center;">
          <p style="color: #b63a36; font-family: 'Montserrat', sans-serif; font-size: 16px; margin-bottom: 16px;">
            Sorry, we couldn't load this product.
          </p>
          <a href="/products/${productHandle}" 
             style="display: inline-block; padding: 10px 24px; background: #080900; color: #ffffff; text-decoration: none; border-radius: 4px; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 600;">
            View Full Product Page
          </a>
        </div>
      `;
    }
  }

  close() {
    this.modal.classList.remove('active');
    document.body.classList.remove('modal-open');
    
    // Clear content after animation
    setTimeout(() => {
      if (!this.modal.classList.contains('active')) {
        this.modalContent.innerHTML = `
          <div class="product-quick-view-modal__loading">
            <div class="product-quick-view-modal__spinner"></div>
          </div>
        `;
      }
    }, 300);

    this.currentProductHandle = null;

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('quickview:closed'));
  }

  initializeProductScripts() {
    // Re-initialize ProductInfo class if it exists
    const productInfoElement = this.modalContent.querySelector('.enhanced-product-info');
    if (productInfoElement && window.ProductInfo) {
      new window.ProductInfo(productInfoElement);
    }

    // Re-initialize any Shopify scripts
    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }

    // Trigger any custom initialization events
    const scripts = this.modalContent.querySelectorAll('script');
    scripts.forEach(script => {
      if (script.src) {
        // External script - reload it
        const newScript = document.createElement('script');
        newScript.src = script.src;
        script.parentNode.replaceChild(newScript, script);
      } else {
        // Inline script - execute it
        try {
          eval(script.textContent);
        } catch (e) {
          console.warn('Error executing inline script:', e);
        }
      }
    });
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productQuickView = new ProductQuickView();
  });
} else {
  window.productQuickView = new ProductQuickView();
}

